import React, { useState, useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';

interface Option {
  id?: string;
  value: string;
  label: string;
}

interface DropdownProps {
  id: string;
  name: string;
  placeholder?: string;
  options: Option[];
  className?: string;
  width?: string;
  textClass?: string;
  onSelect: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  value: string;
  required?: boolean;
  disabled: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  name,
  placeholder = 'Select an option',
  options,
  className = '',
  width = 'w-full',
  textClass = '',
  onSelect,
  value,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleOptionClick = (option: Option) => {
    if (!disabled) {
      const fakeEvent = {
        target: {
          name,
          value: option.id,
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;

      onSelect(fakeEvent);
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedLabel =
    options.find((option) => option.id === value)?.label || placeholder;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md cursor-pointer ${
          disabled ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white'
        } ${textClass} ${width}`}
        onClick={toggleDropdown}
      >
        <span className={`text-gray-700 ${textClass}`}>{selectedLabel}</span>
        <ArrowDown
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>
      {isOpen && !disabled && (
        <div
          className={`absolute z-10 mt-1 bg-white border border-gray-300 rounded-md shadow-lg ${width}`}
        >
          <div className="max-h-60 overflow-y-auto dropdown-scrollbar">
            {options.map((option) => (
              <div
                key={option.id}
                onClick={() => handleOptionClick(option)}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-200 ${
                  value === option.id ? 'bg-gray-100' : ''
                }`}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
